/**
 * 手机号白名单管理 — 销售角色
 */
import { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, Upload } from 'lucide-react';
import { api } from '../services/api';
import { useToast } from '../components/Toast';
import * as XLSX from 'xlsx';

export default function PhoneWhitelist() {
  const { showToast } = useToast();
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ phone: '', customer_name: '', contact_name: '', position: '' });
  const [adding, setAdding] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try { const r = await api.request<any[]>('/promotions/whitelist'); setList(r.data||[]); }
    catch { setList([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchList(); }, []);

  const handleAdd = async () => {
    const phone = form.phone.trim();
    if (!phone || phone.length !== 11) { showToast('warning', '请输入正确的手机号'); return; }
    setAdding(true);
    try {
      const r = await api.request('/promotions/whitelist', { method:'POST', body:JSON.stringify({ phone, customer_name:form.customer_name.trim(), contact_name:form.contact_name.trim(), position:form.position.trim() }) });
      if (r.code === 200) { showToast('success', r.message); setForm({ phone:'', customer_name:'', contact_name:'', position:'' }); fetchList(); }
      else showToast('error', r.message);
    } catch { showToast('error', '添加失败'); }
    finally { setAdding(false); }
  };

  const handleDelete = async (id: number) => {
    setDeleteTarget(list.find((r:any)=>r.id===id));
  };
  const [importing, setImporting] = useState(false);
  const handleImportExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'binary' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        if (rows.length < 2) { showToast('warning', 'Excel 文件为空'); setImporting(false); return; }
        // 表头映射（支持中文列名）
        const header = rows[0].map((h: string) => String(h||'').trim());
        const idxPhone = header.findIndex((h: string) => h.includes('手机号'));
        const idxCust = header.findIndex((h: string) => h.includes('客户名称') || h.includes('公司'));
        const idxName = header.findIndex((h: string) => h.includes('姓名') || h.includes('联系人'));
        const idxPos = header.findIndex((h: string) => h.includes('职务') || h.includes('职位'));
        if (idxPhone < 0) { showToast('error', 'Excel 缺少"手机号"列'); setImporting(false); return; }
        // 解析数据行
        const entries = rows.slice(1).filter((row: any[]) => row[idxPhone] && String(row[idxPhone]).trim()).map((row: any[]) => ({
          phone: String(row[idxPhone] || '').replace(/\D/g, ''),
          customer_name: idxCust >= 0 ? String(row[idxCust] || '').trim() : '',
          contact_name: idxName >= 0 ? String(row[idxName] || '').trim() : '',
          position: idxPos >= 0 ? String(row[idxPos] || '').trim() : '',
        }));
        if (entries.length === 0) { showToast('warning', '没有有效数据'); setImporting(false); return; }
        const r = await api.request('/promotions/whitelist', { method: 'POST', body: JSON.stringify({ entries }) });
        if (r.code === 200) { showToast('success', `导入完成：成功 ${r.data?.added||0} 条，跳过 ${r.data?.skipped||0} 条`); fetchList(); }
        else showToast('error', r.message);
      } catch (err) { showToast('error', '解析 Excel 失败'); }
      finally { setImporting(false); e.target.value = ''; }
    };
    reader.readAsBinaryString(file);
  };

  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);
    try {
      const r = await api.request(`/promotions/whitelist/${id}`, { method:'DELETE' });
      if (r.code === 200) { showToast('success', '已删除'); fetchList(); }
      else showToast('error', r.message);
    } catch { showToast('error', '删除失败'); }
  };

  return (
    <div style={{padding:'16px 24px',maxWidth:900,margin:'0 auto'}}>
      <h1 style={{fontSize:'1.25rem',fontWeight:700,marginBottom:20}}>手机号白名单</h1>

      {/* 添加表单 */}
      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap',alignItems:'flex-end'}}>
        <div style={{display:'flex',flexDirection:'column',gap:4,flex:'1 1 160px'}}>
          <label style={{fontSize:'.75rem',color:'var(--ink-400)'}}>客户名称</label>
          <input value={form.customer_name} onChange={e=>setForm({...form,customer_name:e.target.value})} placeholder="如：盛源建材" style={{padding:'8px 10px',borderRadius:6,border:'1px solid var(--ink-200)',fontSize:'.8125rem'}} />
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:4,flex:'1 1 120px'}}>
          <label style={{fontSize:'.75rem',color:'var(--ink-400)'}}>姓名</label>
          <input value={form.contact_name} onChange={e=>setForm({...form,contact_name:e.target.value})} placeholder="联系人" style={{padding:'8px 10px',borderRadius:6,border:'1px solid var(--ink-200)',fontSize:'.8125rem'}} />
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:4,flex:'1 1 120px'}}>
          <label style={{fontSize:'.75rem',color:'var(--ink-400)'}}>职务</label>
          <input value={form.position} onChange={e=>setForm({...form,position:e.target.value})} placeholder="如：采购经理" style={{padding:'8px 10px',borderRadius:6,border:'1px solid var(--ink-200)',fontSize:'.8125rem'}} />
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:4,flex:'1 1 140px'}}>
          <label style={{fontSize:'.75rem',color:'var(--ink-400)'}}>手机号 *</label>
          <input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value.replace(/\D/g,'')})} maxLength={11} placeholder="11位手机号" style={{padding:'8px 10px',borderRadius:6,border:'1px solid var(--ink-200)',fontSize:'.8125rem'}} />
        </div>
        <button onClick={handleAdd} disabled={adding} style={{padding:'8px 20px',borderRadius:6,border:'none',background:'var(--cinnabar-500)',color:'#fff',cursor:'pointer',fontSize:'.8125rem',whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:4,height:34}}>
          {adding?<Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/>:<Plus size={14}/>} 添加
        </button>
        <label style={{padding:'8px 16px',borderRadius:6,border:'1px solid var(--ink-200)',background:'#fff',cursor:'pointer',fontSize:'.8125rem',whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:4,height:34,color:'var(--ink-500)'}}>
          {importing?<Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/>:<Upload size={14}/>} 导入Excel
          <input type="file" accept=".xlsx,.xls" onChange={handleImportExcel} style={{display:'none'}} />
        </label>
        <div style={{fontSize:'.6875rem',color:'var(--ink-400)',alignSelf:'flex-end',paddingBottom:4}}>表头：客户名称、姓名、职务、手机号</div>
      </div>

      {/* 列表 */}
      {loading ? <div style={{textAlign:'center',padding:40,color:'var(--ink-300)'}}><Loader2 size={24} style={{animation:'spin 1s linear infinite'}}/></div> :
       list.length===0 ? <div style={{textAlign:'center',padding:40,color:'var(--ink-300)',fontSize:'.8125rem'}}>暂无记录</div> :
       <div style={{borderRadius:12,border:'1px solid var(--ink-100)',overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'.8125rem'}}>
          <thead><tr style={{background:'#f9fafb',textAlign:'left'}}>
            <th style={{padding:'10px 16px',fontWeight:500,color:'var(--ink-400)'}}>客户名称</th>
            <th style={{padding:'10px 16px',fontWeight:500,color:'var(--ink-400)'}}>姓名</th>
            <th style={{padding:'10px 16px',fontWeight:500,color:'var(--ink-400)'}}>职务</th>
            <th style={{padding:'10px 16px',fontWeight:500,color:'var(--ink-400)'}}>手机号</th>
            <th style={{padding:'10px 16px',fontWeight:500,color:'var(--ink-400)'}}>状态</th>
            <th style={{padding:'10px 16px',fontWeight:500,color:'var(--ink-400)'}}>操作</th>
          </tr></thead>
          <tbody>
            {list.map((r:any)=>(
              <tr key={r.id} style={{borderTop:'1px solid var(--ink-50)'}}>
                <td style={{padding:'10px 16px'}}>{r.customer_name||'-'}</td>
                <td style={{padding:'10px 16px'}}>{r.contact_name||'-'}</td>
                <td style={{padding:'10px 16px'}}>{r.position||'-'}</td>
                <td style={{padding:'10px 16px'}}>{r.phone}</td>
                <td style={{padding:'10px 16px'}}>
                  <span style={{fontSize:'.6875rem',padding:'2px 8px',borderRadius:10,fontWeight:500,
                    background:r.status===1?'#dcfce7':'#fef3c7',color:r.status===1?'#16a34a':'#d97706'}}>
                    {r.status===1?'已激活':'待激活'}
                  </span>
                </td>
                <td style={{padding:'10px 16px'}}>
                  <button onClick={()=>handleDelete(r.id)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--ink-400)',padding:4}}><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      {/* 删除确认弹窗 */}
      {deleteTarget && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} onClick={()=>setDeleteTarget(null)}>
          <div style={{background:'#fff',borderRadius:16,padding:'24px 28px',maxWidth:400,width:'100%',boxShadow:'0 16px 48px rgba(0,0,0,.12)'}} onClick={e=>e.stopPropagation()}>
            <h3 style={{fontSize:'1rem',fontWeight:600,marginBottom:8}}>确认删除</h3>
            <p style={{fontSize:'.8125rem',color:'var(--ink-500)',marginBottom:8}}>手机号 <strong>{deleteTarget.phone}</strong></p>
            <p style={{fontSize:'.75rem',color:'#ef4444',marginBottom:20}}>删除后将同时移除关联的客户用户，互动记录不会丢失。</p>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setDeleteTarget(null)} style={{padding:'8px 20px',borderRadius:8,border:'1px solid var(--ink-200)',background:'#fff',cursor:'pointer',fontSize:'.8125rem'}}>取消</button>
              <button onClick={confirmDelete} style={{padding:'8px 20px',borderRadius:8,border:'none',background:'#ef4444',color:'#fff',cursor:'pointer',fontSize:'.8125rem'}}>确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
